'use client';

/**
 * useCheckoutLifecycle Hook
 *
 * Manages checkout session lifecycle:
 * - create: Start a new checkout session
 * - restore: Restore from storage (with race condition prevention)
 * - refresh: Fetch latest state from server
 * - clear: Clear checkout state
 *
 * ARCHITECTURE UPDATE (2026-01):
 * - Now uses BFF API (/api/checkout/[id]) instead of direct Saleor calls
 * - Integrates with per-channel state for multi-region cart preservation
 * - Supports lazy restore (only fetch when needed)
 */

import { useCallback, useRef } from 'react';
import { useSnapshot } from 'valtio';

import { fetchCheckoutFromBFF } from '@/lib/checkout/api';
import { getCheckoutCookie } from '@/lib/checkout/cookies.client';
import { checkoutCreate } from '@/lib/checkout/operations';
import type { CheckoutData } from '@/lib/checkout/types';
import { getChannelFromLocale } from '@/lib/configuration/channels';
import { checkoutActions, checkoutState } from '@/states/checkout';
import {
  getChannelSnapshot,
  saveToChannelSnapshot,
  hasValidCachedData,
  clearChannelSnapshot,
  setActiveChannel,
} from '@/states/checkout/channelState';

/**
 * Lifecycle operation result
 */
export interface LifecycleOperationResult {
  checkout: CheckoutData | null;
  isExpired?: boolean;
  orderId?: string;
  paymentMethod?: unknown;
  isChangedAfterOrderCreated?: boolean;
}

/**
 * Hook options
 */
export interface UseCheckoutLifecycleOptions {
  locale?: string;
}

/**
 * Focused hook for checkout session lifecycle
 */
export function useCheckoutLifecycle(options: UseCheckoutLifecycleOptions = {}) {
  const snapshot = useSnapshot(checkoutState);

  // Race condition prevention for restore
  const ongoingRestoreRef = useRef<Promise<LifecycleOperationResult> | null>(null);

  // Parse locale
  const locale = options.locale || 'us-en';
  const [, languageSlug = 'en'] = locale.split('-');
  const channelSlug = getChannelFromLocale(locale);

  /**
   * Create a new checkout session
   */
  const create = useCallback(
    async (force = false): Promise<CheckoutData | null> => {
      if (checkoutState.id && !force) {
        return checkoutState.checkout;
      }

      checkoutActions.setProcessing(true);
      checkoutActions.clearErrors();

      try {
        const { checkout, errors } = await checkoutCreate({
          channelSlug,
          languageCode: languageSlug.toUpperCase(),
        });

        if (errors?.length) {
          checkoutActions.setErrors(errors, 'checkout');
          return null;
        }

        if (checkout) {
          checkoutActions.updateCheckout(checkout);
          checkoutActions.setChannel(channelSlug);
          return checkout;
        }
      } finally {
        checkoutActions.setProcessing(false);
      }
      return null;
    },
    [channelSlug, languageSlug]
  );

  /**
   * Restore existing checkout from storage
   *
   * Now uses BFF API and per-channel state:
   * 1. Check if we have valid cached data for this channel
   * 2. If yes, restore from cache (no API call)
   * 3. If no, fetch via BFF API and cache the result
   *
   * @param options.force - Force fetch even if cached data exists
   * @param options.checkoutId - Explicit checkout ID to restore
   */
  const restore = useCallback(
    async (options?: {
      force?: boolean;
      checkoutId?: string;
    }): Promise<LifecycleOperationResult> => {
      const { force = false, checkoutId } = options || {};

      // Prevent concurrent restores
      if (ongoingRestoreRef.current) {
        return ongoingRestoreRef.current;
      }

      const restorePromise = (async (): Promise<LifecycleOperationResult> => {
        // Set active channel
        setActiveChannel(channelSlug);

        // Check if we have valid cached data (skip API call)
        if (!force && hasValidCachedData(channelSlug)) {
          const cached = getChannelSnapshot(channelSlug);
          if (cached.checkout) {
            // Restore from cache
            checkoutActions.updateCheckout(cached.checkout);
            checkoutActions.setChannel(channelSlug);
            checkoutActions.setInitializing(false);
            return { checkout: cached.checkout, isExpired: false };
          }
        }

        // Priority for checkout ID: param > state > cache > cookie
        const channelSnapshot = getChannelSnapshot(channelSlug);
        const storedId =
          checkoutId ||
          checkoutState.id ||
          channelSnapshot.id ||
          (typeof window !== 'undefined' ? getCheckoutCookie(channelSlug) : null);

        if (!storedId) {
          console.log('[useCheckoutLifecycle DEBUG] No stored ID, returning null');
          checkoutActions.setInitializing(false);
          return { checkout: null, isExpired: false };
        }

        console.log('[useCheckoutLifecycle DEBUG] Fetching from BFF:', storedId.slice(0, 8));
        checkoutActions.setInitializing(true);
        checkoutActions.setProcessing(true);

        try {
          // Fetch via BFF API (not direct Saleor call)
          const result = await fetchCheckoutFromBFF(storedId, locale);

          console.log('[useCheckoutLifecycle DEBUG] BFF result:', {
            hasCheckout: !!result.checkout,
            linesCount: result.checkout?.lines?.length || 0,
            isExpired: result.isExpired,
            error: result.error,
          });

          if (result.checkout) {
            // Update main state
            checkoutActions.updateCheckout(result.checkout);
            checkoutActions.setChannel(channelSlug);

            // Save to per-channel cache
            saveToChannelSnapshot(channelSlug, {
              id: storedId,
              checkout: result.checkout,
              email: result.checkout.email || null,
              deliveryMethodId: result.checkout.deliveryMethod?.id || null,
            });

            console.log(
              '[useCheckoutLifecycle DEBUG] Updated state, lines:',
              checkoutState.lines.length
            );
            return { checkout: result.checkout, isExpired: false };
          } else if (result.isExpired) {
            // Checkout expired - clear state and cache
            console.log('[useCheckoutLifecycle DEBUG] Checkout expired, clearing');
            checkoutActions.clear();
            clearChannelSnapshot(channelSlug);
            return { checkout: null, isExpired: true };
          }

          console.log('[useCheckoutLifecycle DEBUG] No checkout in result');
          return { checkout: null, isExpired: false };
        } finally {
          checkoutActions.setProcessing(false);
          checkoutActions.setInitializing(false);
        }
      })();

      ongoingRestoreRef.current = restorePromise;
      try {
        return await restorePromise;
      } finally {
        ongoingRestoreRef.current = null;
      }
    },
    [channelSlug, locale]
  );

  /**
   * Refresh checkout data from server
   * Uses BFF API to get latest checkout state
   */
  const refresh = useCallback(async (): Promise<CheckoutData | null> => {
    const checkoutId = checkoutState.id;
    if (!checkoutId) return null;

    checkoutActions.setProcessing(true);

    try {
      // Fetch via BFF API
      const result = await fetchCheckoutFromBFF(checkoutId, locale);

      if (result.checkout) {
        checkoutActions.updateCheckout(result.checkout);

        // Update channel cache
        saveToChannelSnapshot(channelSlug, {
          id: checkoutId,
          checkout: result.checkout,
          email: result.checkout.email || null,
          deliveryMethodId: result.checkout.deliveryMethod?.id || null,
        });

        return result.checkout;
      }

      return null;
    } finally {
      checkoutActions.setProcessing(false);
    }
  }, [channelSlug, locale]);

  /**
   * Clear the checkout session (both main state and channel cache)
   */
  const clear = useCallback(() => {
    checkoutActions.clear();
    clearChannelSnapshot(channelSlug);
  }, [channelSlug]);

  return {
    // State
    id: snapshot.id,
    checkout: snapshot.checkout,
    isInitializing: snapshot.isInitializing,
    isProcessing: snapshot.isProcessing,
    isSyncing: snapshot.isSyncing,
    channelSlug: snapshot.channelSlug,

    // Lifecycle operations
    create,
    restore,
    refresh,
    clear,
  };
}
